import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Bed, Users, Hash, Percent, BedDouble, ImagePlus, X, TrendingUp, Loader2, Lock, Clock, Tag } from 'lucide-react';
import {
  getRoomTypes, getRoomClasses, addRoomType, updateRoomType, deleteRoomType,
  setRoomDiscount, getDiscountLogs,
  addPhysicalRooms, getBeds, uploadRoomTypeImages, getRoomTypeImages, deleteRoomImages,
  getRoomFacilities, addRoomFacility, updateRoomFacility, deleteRoomFacility,
  getPricingByRoomType, addPricing, updatePricing, deletePricing,
  getPhysicalRooms, getRoomBlocks, blockRoom, unblockRoom,
  getHotelInfo,
} from '../services/adminService';

const MAX_IMAGES = 5;

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};


const EMPTY_TYPE_FORM = {
  name: '', code: '', roomClassId: '',
  maxAdults: '', maxChildren: '',
  basePrice: '', discountPercent: '0', taxPercent: '10', serviceChargePercent: '0', description: '',
  checkInTime: '11:00', checkOutTime: '11:00',
};

const EMPTY_BED_ROW = { bedId: '', count: '1' };

const EMPTY_ROOM_FORM = { startRoomNumber: '', quantity: '', floor: '' };
const EMPTY_PRICING_FORM = { label: '', startDate: '', endDate: '', pricePerNight: '' };
const EMPTY_BLOCK_FORM = { fromDate: '', toDate: '', reason: '' };

const Rooms = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomClasses, setRoomClasses] = useState([]);
  const [beds, setBeds] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Feature / Amenity / Rules / Beds state for add/edit dialog
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [selectedAmenityIds, setSelectedAmenityIds] = useState([]);
  const [typeRules, setTypeRules] = useState([]);
  const [typeBeds, setTypeBeds] = useState([{ ...EMPTY_BED_ROW }]);

  // Manage Facilities dialog state
  const [isFacilityDialogOpen, setIsFacilityDialogOpen] = useState(false);
  const [facilityForm, setFacilityForm] = useState({ name: '', icon: '', type: 0 });
  const [editingFacility, setEditingFacility] = useState(null);
  const [facilityDialogSaving, setFacilityDialogSaving] = useState(false);

  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [editingType, setEditingType] = useState(null);
  const [addingRoomsFor, setAddingRoomsFor] = useState(null);
  const [deletingType, setDeletingType] = useState(null);

  const [isDynamicPricingEnabled, setIsDynamicPricingEnabled] = useState(false);

  // Pricing state
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [pricingRoomType, setPricingRoomType] = useState(null);
  const [pricingRules, setPricingRules] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState(EMPTY_PRICING_FORM);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [deletingPricingId, setDeletingPricingId] = useState(null);

  const [typeForm, setTypeForm] = useState(EMPTY_TYPE_FORM);
  const [roomForm, setRoomForm] = useState(EMPTY_ROOM_FORM);

  // Block state
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [blockingRoomType, setBlockingRoomType] = useState(null);
  const [allPhysicalRooms, setAllPhysicalRooms] = useState(null);
  const [physicalRoomsLoading, setPhysicalRoomsLoading] = useState(false);
  const [selectedBlockRoomId, setSelectedBlockRoomId] = useState('');
  const [currentRoomBlocks, setCurrentRoomBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [blockForm, setBlockForm] = useState(EMPTY_BLOCK_FORM);
  const [blockSaving, setBlockSaving] = useState(false);
  const [unblockingId, setUnblockingId] = useState(null);

  // Image state for Add/Edit dialog
  const [existingImages, setExistingImages] = useState([]); // { id, url, fileName }[]
  const [removedImageIds, setRemovedImageIds] = useState([]); // DB ids to delete on save
  const [newImageFiles, setNewImageFiles] = useState([]); // File[]
  const [newImagePreviews, setNewImagePreviews] = useState([]); // blob: URLs
  const imageInputRef = useRef(null);

  // Discount quick-set state (per room type)
  const [discountInputs, setDiscountInputs]   = useState({});   // { [rtId]: string }
  const [discountSaving, setDiscountSaving]   = useState(null); // rtId being saved
  const [discountLogs, setDiscountLogs]       = useState({});   // { [rtId]: VMRoomDiscountLog[] }
  const [expandedLog, setExpandedLog]         = useState(null); // rtId with open log panel

  const { toast } = useToast();

  const loadData = useCallback(async () => {
    try {
      const [types, classes, bedList, facilities, hotelInfo] = await Promise.all([
        getRoomTypes(), getRoomClasses(), getBeds(), getRoomFacilities(), getHotelInfo(),
      ]);
      setRoomTypes(types);
      setRoomClasses(classes);
      setBeds(bedList);
      setAllFacilities(facilities);
      setIsDynamicPricingEnabled(hotelInfo?.isDynamicPricingEnabled ?? false);
    } catch {
      toast({ title: 'Failed to load rooms', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSetDiscount = async (rtId, currentDiscount) => {
    const val = parseFloat(discountInputs[rtId] ?? currentDiscount) || 0;
    setDiscountSaving(rtId);
    try {
      await setRoomDiscount(rtId, Math.min(val, 100));
      setRoomTypes(prev => prev.map(rt => rt.id === rtId ? { ...rt, discountPercent: val } : rt));
      // Refresh log
      const logs = await getDiscountLogs(rtId);
      setDiscountLogs(p => ({ ...p, [rtId]: logs }));
      if (expandedLog !== rtId) setExpandedLog(rtId);
      toast({ title: `Discount set to ${val}%` });
    } catch (e) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setDiscountSaving(null);
    }
  };

  const handleToggleLog = async (rtId) => {
    if (expandedLog === rtId) { setExpandedLog(null); return; }
    setExpandedLog(rtId);
    if (!discountLogs[rtId]) {
      try {
        const logs = await getDiscountLogs(rtId);
        setDiscountLogs(p => ({ ...p, [rtId]: logs }));
      } catch { setDiscountLogs(p => ({ ...p, [rtId]: [] })); }
    }
  };

  const resetImageState = () => {
    setExistingImages([]);
    setRemovedImageIds([]);
    setNewImageFiles([]);
    newImagePreviews.forEach(url => URL.revokeObjectURL(url));
    setNewImagePreviews([]);
  };

  const openAddType = () => {
    setEditingType(null);
    setTypeForm(EMPTY_TYPE_FORM);
    setSelectedFeatureIds([]);
    setSelectedAmenityIds([]);
    setTypeRules([]);
    setTypeBeds([{ ...EMPTY_BED_ROW }]);
    resetImageState();
    setIsTypeDialogOpen(true);
  };

  const openEditType = async (rt) => {
    setEditingType(rt);
    setTypeForm({
      name: rt.name,
      code: rt.code,
      roomClassId: String(rt.roomClassId),
      maxAdults: String(rt.maxAdults),
      maxChildren: String(rt.maxChildren),
      basePrice: String(rt.basePrice),
      discountPercent: String(rt.discountPercent ?? 0),
      taxPercent: String(rt.taxPercent),
      serviceChargePercent: String(rt.serviceChargePercent),
      description: rt.description || '',
      checkInTime: rt.checkInTime || '11:00',
      checkOutTime: rt.checkOutTime || '11:00',
    });
    setSelectedFeatureIds((rt.features || []).map(f => f.id));
    setSelectedAmenityIds((rt.amenities || []).map(a => a.id));
    setTypeRules(rt.rules || []);
    setTypeBeds(
      rt.beds && rt.beds.length > 0
        ? rt.beds.map(b => ({ bedId: String(b.bedId), count: String(b.count) }))
        : [{ ...EMPTY_BED_ROW }]
    );
    resetImageState();
    setIsTypeDialogOpen(true);
    try {
      const imgs = await getRoomTypeImages(rt.id);
      setExistingImages(imgs);
    } catch {
      // non-fatal — images just won't show
    }
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: typeForm.name,
      code: typeForm.code.toUpperCase(),
      roomClassId: parseInt(typeForm.roomClassId),
      maxAdults: parseInt(typeForm.maxAdults),
      maxChildren: parseInt(typeForm.maxChildren) || 0,
      basePrice: parseFloat(typeForm.basePrice),
      discountPercent: Math.min(parseFloat(typeForm.discountPercent) || 0, 100),
      taxPercent: parseFloat(typeForm.taxPercent) || 0,
      serviceChargePercent: parseFloat(typeForm.serviceChargePercent) || 0,
      description: typeForm.description,
      checkInTime: typeForm.checkInTime || null,
      checkOutTime: typeForm.checkOutTime || null,
      beds: typeBeds
        .filter(b => b.bedId && parseInt(b.count) > 0)
        .map(b => ({ bedId: parseInt(b.bedId), count: parseInt(b.count) || 1 })),
      featureIds: selectedFeatureIds,
      amenityIds: selectedAmenityIds,
      rules: typeRules.filter(r => r.trim()),
    };
    try {
      let roomTypeId;
      if (editingType) {
        await updateRoomType(editingType.id, payload);
        roomTypeId = editingType.id;
        toast({ title: 'Room type updated' });
      } else {
        roomTypeId = await addRoomType(payload);
        toast({ title: 'Room type added' });
      }

      if (removedImageIds.length > 0) {
        await deleteRoomImages(removedImageIds);
      }
      if (newImageFiles.length > 0) {
        await uploadRoomTypeImages(roomTypeId, newImageFiles);
      }

      setIsTypeDialogOpen(false);
      await loadData();
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (rt) => {
    setDeletingType(rt);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteType = async () => {
    setSaving(true);
    try {
      await deleteRoomType(deletingType.id);
      toast({ title: 'Room type deleted' });
      setIsDeleteDialogOpen(false);
      await loadData();
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const openAddRooms = (rt) => {
    setAddingRoomsFor(rt);
    setRoomForm(EMPTY_ROOM_FORM);
    setIsRoomDialogOpen(true);
  };

  const handleAddRooms = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addPhysicalRooms({
        roomTypeId: addingRoomsFor.id,
        startRoomNumber: parseInt(roomForm.startRoomNumber),
        quantity: parseInt(roomForm.quantity),
        floor: roomForm.floor || null,
        notes: null,
        facilities: [],
      });
      toast({ title: `${roomForm.quantity} room(s) added` });
      setIsRoomDialogOpen(false);
      await loadData();
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const activeExistingImages = existingImages.filter(img => !removedImageIds.includes(img.id));
  const totalImageCount = activeExistingImages.length + newImageFiles.length;

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const available = MAX_IMAGES - totalImageCount;
    const toAdd = files.slice(0, available);
    setNewImageFiles(prev => [...prev, ...toAdd]);
    setNewImagePreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeExistingImage = (id) => {
    setRemovedImageIds(prev => [...prev, id]);
  };

  const removeNewImage = (index) => {
    URL.revokeObjectURL(newImagePreviews[index]);
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const openFacilityDialog = () => {
    setEditingFacility(null);
    setFacilityForm({ name: '', icon: '', type: 0 });
    setIsFacilityDialogOpen(true);
  };

  const openEditFacility = (f) => {
    setEditingFacility(f);
    setFacilityForm({ name: f.name, icon: f.icon || '', type: f.type ?? 0 });
  };

  const handleSaveFacility = async (e) => {
    e.preventDefault();
    setFacilityDialogSaving(true);
    try {
      if (editingFacility) {
        await updateRoomFacility(editingFacility.id, facilityForm);
        toast({ title: 'Facility updated' });
      } else {
        await addRoomFacility(facilityForm);
        toast({ title: 'Facility added' });
      }
      setEditingFacility(null);
      setFacilityForm({ name: '', icon: '' });
      const updated = await getRoomFacilities();
      setAllFacilities(updated);
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setFacilityDialogSaving(false);
    }
  };

  const handleDeleteFacility = async (id) => {
    try {
      await deleteRoomFacility(id);
      toast({ title: 'Facility deleted' });
      const updated = await getRoomFacilities();
      setAllFacilities(updated);
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    }
  };

  const openBlockDialog = async (rt) => {
    setBlockingRoomType(rt);
    setSelectedBlockRoomId('');
    setCurrentRoomBlocks([]);
    setBlockForm(EMPTY_BLOCK_FORM);
    setIsBlockDialogOpen(true);
    if (!allPhysicalRooms) {
      setPhysicalRoomsLoading(true);
      try {
        const rooms = await getPhysicalRooms();
        setAllPhysicalRooms(rooms);
      } catch {
        toast({ title: 'Failed to load rooms', variant: 'destructive' });
      } finally {
        setPhysicalRoomsLoading(false);
      }
    }
  };

  const handleSelectBlockRoom = async (roomId) => {
    setSelectedBlockRoomId(roomId);
    setCurrentRoomBlocks([]);
    if (!roomId) return;
    setBlocksLoading(true);
    try {
      const blocks = await getRoomBlocks(parseInt(roomId));
      setCurrentRoomBlocks(blocks);
    } catch {
      toast({ title: 'Failed to load blocks', variant: 'destructive' });
    } finally {
      setBlocksLoading(false);
    }
  };

  const handleBlockRoom = async (e) => {
    e.preventDefault();
    setBlockSaving(true);
    try {
      await blockRoom(parseInt(selectedBlockRoomId), blockForm.fromDate, blockForm.toDate, blockForm.reason || null);
      toast({ title: 'Room blocked' });
      setBlockForm(EMPTY_BLOCK_FORM);
      const blocks = await getRoomBlocks(parseInt(selectedBlockRoomId));
      setCurrentRoomBlocks(blocks);
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setBlockSaving(false);
    }
  };

  const handleUnblock = async (blockId) => {
    setUnblockingId(blockId);
    try {
      await unblockRoom(parseInt(selectedBlockRoomId), blockId);
      toast({ title: 'Room unblocked' });
      setCurrentRoomBlocks(prev => prev.filter(b => b.id !== blockId));
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setUnblockingId(null);
    }
  };

  const setTypeField = (field) => (e) =>
    setTypeForm(prev => ({ ...prev, [field]: e.target.value }));

  const setRoomField = (field) => (e) =>
    setRoomForm(prev => ({ ...prev, [field]: e.target.value }));

  const openPricingDialog = async (rt) => {
    setPricingRoomType(rt);
    setPricingRules([]);
    setShowPricingForm(false);
    setEditingPricing(null);
    setPricingForm(EMPTY_PRICING_FORM);
    setIsPricingDialogOpen(true);
    setPricingLoading(true);
    try {
      const rules = await getPricingByRoomType(rt.id);
      setPricingRules(rules);
    } catch {
      toast({ title: 'Failed to load pricing rules', variant: 'destructive' });
    } finally {
      setPricingLoading(false);
    }
  };

  const openEditPricingRule = (rule) => {
    setEditingPricing(rule);
    setPricingForm({
      label: rule.label || '',
      startDate: rule.startDate.substring(0, 10),
      endDate: rule.endDate.substring(0, 10),
      pricePerNight: String(rule.pricePerNight),
    });
    setShowPricingForm(true);
  };

  const cancelPricingForm = () => {
    setShowPricingForm(false);
    setEditingPricing(null);
    setPricingForm(EMPTY_PRICING_FORM);
  };

  const handleSavePricing = async (e) => {
    e.preventDefault();
    setPricingSaving(true);
    const payload = {
      roomTypeId: pricingRoomType.id,
      pricePerNight: parseFloat(pricingForm.pricePerNight),
      startDate: pricingForm.startDate,
      endDate: pricingForm.endDate,
      label: pricingForm.label || null,
    };
    try {
      if (editingPricing) {
        await updatePricing(editingPricing.id, payload);
        toast({ title: 'Rule updated' });
      } else {
        await addPricing(payload);
        toast({ title: 'Rule added' });
      }
      const rules = await getPricingByRoomType(pricingRoomType.id);
      setPricingRules(rules);
      cancelPricingForm();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setPricingSaving(false);
    }
  };

  const handleDeletePricing = async (id) => {
    setDeletingPricingId(id);
    try {
      await deletePricing(id);
      setPricingRules(prev => prev.filter(r => r.id !== id));
      toast({ title: 'Rule deleted' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingPricingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Room Management</h1>
          <p className="text-slate-400 mt-1">Manage room types and physical rooms</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="bg-slate-800/50 border-slate-700 animate-pulse">
              <CardContent className="p-6 h-48" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Room Management</h1>
          <p className="text-slate-400 mt-1">Manage room types and physical rooms</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openFacilityDialog} variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700">
            Manage Features & Amenities
          </Button>
          <Button onClick={openAddType} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />Add Room Type
          </Button>
        </div>
      </div>

      {roomTypes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roomTypes.map((rt) => (
            <Card key={rt.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all duration-200">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-white">{rt.name}</h3>
                      <Badge className="text-xs bg-slate-600 text-slate-200">{rt.code}</Badge>
                    </div>
                    <p className="text-sm text-slate-400 mt-0.5">
                      {rt.roomClassName}{rt.beds && rt.beds.length > 0 ? ' · ' + rt.beds.map(b => `${b.count} ${b.bedName}`).join(', ') : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    {rt.discountPercent > 0 && (
                      <p className="text-xs text-slate-400 line-through">৳{rt.basePrice.toLocaleString()}</p>
                    )}
                    <p className="text-xl font-bold text-white">
                      {rt.discountPercent > 0
                        ? `৳${Math.round(rt.basePrice * (1 - rt.discountPercent / 100)).toLocaleString()}`
                        : `৳${rt.basePrice.toLocaleString()}`
                      }
                    </p>
                    {rt.discountPercent > 0 && (
                      <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded font-medium">
                        {rt.discountPercent}% OFF
                      </span>
                    )}
                    {!rt.discountPercent && <p className="text-xs text-slate-400">per night</p>}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-slate-300">
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{rt.maxAdults}A {rt.maxChildren}C</span>
                  <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{rt.beds ? rt.beds.reduce((s, b) => s + b.count, 0) : 0} bed(s)</span>
                  <span className="flex items-center gap-1"><Percent className="h-3.5 w-3.5" />Tax {rt.taxPercent}%</span>
                  <span className="flex items-center gap-1"><Hash className="h-3.5 w-3.5" />{rt.roomCount} room(s)</span>
                </div>

                {(rt.checkInTime || rt.checkOutTime) && (
                  <div className="flex items-center gap-4 text-xs text-slate-400 bg-slate-700/40 rounded-lg px-3 py-2">
                    {rt.checkInTime && (
                      <span className="flex items-center gap-1.5">
                        <span className="text-green-400 font-medium">Check-in</span>
                        {fmtTime(rt.checkInTime)}
                      </span>
                    )}
                    {rt.checkInTime && rt.checkOutTime && <span className="text-slate-600">→</span>}
                    {rt.checkOutTime && (
                      <span className="flex items-center gap-1.5">
                        <span className="text-red-400 font-medium">Check-out</span>
                        {fmtTime(rt.checkOutTime)}
                      </span>
                    )}
                  </div>
                )}

                {rt.description && (
                  <p className="text-sm text-slate-400 line-clamp-2">{rt.description}</p>
                )}

                {/* ── Discount quick-set ── */}
                <div className="border-t border-slate-700 pt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span className="text-xs text-slate-400">Discount</span>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={discountInputs[rt.id] ?? String(rt.discountPercent ?? 0)}
                      onChange={e => setDiscountInputs(p => ({ ...p, [rt.id]: e.target.value }))}
                      className="bg-slate-700 border-slate-600 h-7 w-20 text-xs text-center px-2"
                      placeholder="0"
                    />
                    <span className="text-slate-400 text-xs">%</span>
                    <Button
                      size="sm"
                      onClick={() => handleSetDiscount(rt.id, rt.discountPercent)}
                      disabled={discountSaving === rt.id}
                      className="h-7 text-xs px-2 bg-amber-600 hover:bg-amber-700"
                    >
                      {discountSaving === rt.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                    </Button>
                    <button
                      onClick={() => handleToggleLog(rt.id)}
                      className={`ml-auto text-slate-500 hover:text-slate-200 transition-colors ${expandedLog === rt.id ? 'text-slate-200' : ''}`}
                      title="Discount history"
                    >
                      <Clock className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {expandedLog === rt.id && (
                    <div className="space-y-1 pl-5">
                      {!discountLogs[rt.id] ? (
                        <div className="flex items-center gap-1 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading...</div>
                      ) : discountLogs[rt.id].length === 0 ? (
                        <p className="text-xs text-slate-500">No discount changes yet.</p>
                      ) : (
                        discountLogs[rt.id].map((log, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-slate-700/40 rounded px-2 py-1">
                            <span className="text-slate-300 font-medium truncate mr-2">{log.changedByName}</span>
                            <span className="text-slate-400 shrink-0">
                              {log.oldDiscount}% → <span className="text-amber-400 font-medium">{log.newDiscount}%</span>
                            </span>
                            <span className="text-slate-500 text-[10px] ml-2 shrink-0">{log.createdAt}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={() => openAddRooms(rt)} className="bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-600/40">
                    <Plus className="h-3.5 w-3.5 mr-1" />Add Rooms
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditType(rt)} className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700">
                    <Edit className="h-3.5 w-3.5 mr-1" />Edit
                  </Button>
                  {isDynamicPricingEnabled && (
                    <Button size="sm" variant="outline" onClick={() => openPricingDialog(rt)} className="border-amber-600/60 text-amber-400 hover:bg-amber-600/20">
                      <TrendingUp className="h-3.5 w-3.5 mr-1" />Pricing
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openBlockDialog(rt)} className="border-orange-600/60 text-orange-400 hover:bg-orange-600/20">
                    <Lock className="h-3.5 w-3.5 mr-1" />Block
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => confirmDelete(rt)} className="border-red-600 text-red-400 hover:bg-red-600/20">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Bed className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Room Types Found</h3>
            <p className="text-slate-400 mb-4">Start by creating your first room type.</p>
            <Button onClick={openAddType} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />Add First Room Type
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add / Edit Room Type Dialog */}
      <Dialog open={isTypeDialogOpen} onOpenChange={setIsTypeDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingType ? 'Update room type details' : 'Create a new room type template'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveType} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={typeForm.name} onChange={setTypeField('name')} className="bg-slate-700 border-slate-600" placeholder="e.g. Deluxe Double" required />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input value={typeForm.code} onChange={setTypeField('code')} className="bg-slate-700 border-slate-600" placeholder="e.g. DLX" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Room Class</Label>
              <Select value={typeForm.roomClassId} onValueChange={(v) => setTypeForm(prev => ({ ...prev, roomClassId: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {roomClasses.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bed Combination */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bed Configuration</Label>
                <button
                  type="button"
                  onClick={() => setTypeBeds(prev => [...prev, { ...EMPTY_BED_ROW }])}
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Add Bed
                </button>
              </div>
              <div className="space-y-2">
                {typeBeds.map((bedRow, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <div className="flex-1">
                      <Select
                        value={bedRow.bedId}
                        onValueChange={(v) => setTypeBeds(prev => prev.map((b, idx) => idx === i ? { ...b, bedId: v } : b))}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600">
                          <SelectValue placeholder="Select bed type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {beds.map(b => (
                            <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={bedRow.count}
                      onChange={e => setTypeBeds(prev => prev.map((b, idx) => idx === i ? { ...b, count: e.target.value } : b))}
                      className="bg-slate-700 border-slate-600 w-20 text-center"
                      placeholder="Qty"
                    />
                    {typeBeds.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setTypeBeds(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-red-400 hover:text-red-300 flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">e.g. 1 Double Bed + 2 Single Beds</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Adults</Label>
                <Input type="number" min="1" value={typeForm.maxAdults} onChange={setTypeField('maxAdults')} className="bg-slate-700 border-slate-600" required />
              </div>
              <div className="space-y-2">
                <Label>Max Children</Label>
                <Input type="number" min="0" value={typeForm.maxChildren} onChange={setTypeField('maxChildren')} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Price (৳)</Label>
                <Input type="number" min="0" step="0.01" value={typeForm.basePrice} onChange={setTypeField('basePrice')} className="bg-slate-700 border-slate-600" required />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  Discount %
                  {parseFloat(typeForm.discountPercent) > 0 && typeForm.basePrice && (
                    <span className="text-xs text-green-400 font-normal">
                      → ৳{Math.round(parseFloat(typeForm.basePrice) * (1 - parseFloat(typeForm.discountPercent) / 100)).toLocaleString()}
                    </span>
                  )}
                </Label>
                <Input type="number" min="0" max="100" step="0.1" value={typeForm.discountPercent} onChange={setTypeField('discountPercent')} className="bg-slate-700 border-slate-600" placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={typeForm.taxPercent} onChange={setTypeField('taxPercent')} className="bg-slate-700 border-slate-600" />
              </div>
              <div className="space-y-2">
                <Label>Service Charge %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={typeForm.serviceChargePercent} onChange={setTypeField('serviceChargePercent')} className="bg-slate-700 border-slate-600" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={typeForm.description} onChange={setTypeField('description')} className="bg-slate-700 border-slate-600" rows={3} />
            </div>

            {/* Check-In / Check-Out Times */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Check-In Time</Label>
                <input
                  type="time"
                  value={typeForm.checkInTime}
                  onChange={setTypeField('checkInTime')}
                  className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                />
              </div>
              <div className="space-y-2">
                <Label>Check-Out Time</Label>
                <input
                  type="time"
                  value={typeForm.checkOutTime}
                  onChange={setTypeField('checkOutTime')}
                  className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Room Features */}
            {(() => {
              const features = allFacilities.filter(f => f.type === 0);
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Room Features <span className="text-slate-400 font-normal text-xs">(shown as tags)</span></Label>
                    {features.length === 0 && (
                      <span className="text-xs text-blue-400 cursor-pointer hover:underline" onClick={openFacilityDialog}>
                        + Add features in Manage
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-700/50 rounded-lg border border-slate-600 min-h-[44px]">
                    {features.length === 0
                      ? <span className="text-slate-500 text-xs self-center">No features defined yet.</span>
                      : features.map(f => {
                          const selected = selectedFeatureIds.includes(f.id);
                          return (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => setSelectedFeatureIds(prev =>
                                selected ? prev.filter(id => id !== f.id) : [...prev, f.id]
                              )}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                selected ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                              }`}
                            >
                              {f.name}
                            </button>
                          );
                        })
                    }
                  </div>
                </div>
              );
            })()}

            {/* Room Amenities */}
            {(() => {
              const amenities = allFacilities.filter(f => f.type === 1);
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Room Amenities <span className="text-slate-400 font-normal text-xs">(shown as cards)</span></Label>
                    {amenities.length === 0 && (
                      <span className="text-xs text-purple-400 cursor-pointer hover:underline" onClick={openFacilityDialog}>
                        + Add amenities in Manage
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-700/50 rounded-lg border border-slate-600 min-h-[44px]">
                    {amenities.length === 0
                      ? <span className="text-slate-500 text-xs self-center">No amenities defined yet.</span>
                      : amenities.map(f => {
                          const selected = selectedAmenityIds.includes(f.id);
                          return (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => setSelectedAmenityIds(prev =>
                                selected ? prev.filter(id => id !== f.id) : [...prev, f.id]
                              )}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                selected ? 'bg-purple-600 text-white' : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                              }`}
                            >
                              {f.name}
                            </button>
                          );
                        })
                    }
                  </div>
                </div>
              );
            })()}

            {/* Room Rules */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Room Rules <span className="text-slate-400 font-normal text-xs">(cancellation, refund, policy etc.)</span></Label>
                <button
                  type="button"
                  onClick={() => setTypeRules(prev => [...prev, ''])}
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Add Rule
                </button>
              </div>
              {typeRules.length === 0 && (
                <p className="text-xs text-slate-500 italic">No rules added yet.</p>
              )}
              <div className="space-y-2">
                {typeRules.map((rule, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={rule}
                      onChange={e => setTypeRules(prev => prev.map((r, idx) => idx === i ? e.target.value : r))}
                      className="bg-slate-700 border-slate-600 text-sm"
                      placeholder={`e.g. Free cancellation up to 24 hours before check-in`}
                    />
                    <button
                      type="button"
                      onClick={() => setTypeRules(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-red-400 hover:text-red-300 px-1 flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Room Images</Label>
                <span className="text-xs text-slate-400">{totalImageCount}/{MAX_IMAGES}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeExistingImages.map((img) => (
                  <div key={img.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-600 group">
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(img.id)}
                      className="absolute top-0.5 right-0.5 bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
                {newImagePreviews.map((preview, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-600 group">
                    <img src={preview} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeNewImage(i)}
                      className="absolute top-0.5 right-0.5 bg-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
                {totalImageCount < MAX_IMAGES && (
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 hover:border-blue-500 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-blue-400 transition-colors"
                  >
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-xs">Add</span>
                  </button>
                )}
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageSelect}
              />
              <p className="text-xs text-slate-500">Upload up to {MAX_IMAGES} images. JPG, PNG accepted.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTypeDialogOpen(false)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Saving...' : (editingType ? 'Update' : 'Add Room Type')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Physical Rooms Dialog */}
      <Dialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Physical Rooms</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add rooms for: <span className="text-white font-medium">{addingRoomsFor?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddRooms} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Room Number</Label>
                <Input type="number" min="1" value={roomForm.startRoomNumber} onChange={setRoomField('startRoomNumber')} className="bg-slate-700 border-slate-600" placeholder="e.g. 101" required />
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" min="1" max="50" value={roomForm.quantity} onChange={setRoomField('quantity')} className="bg-slate-700 border-slate-600" placeholder="e.g. 5" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Floor (optional)</Label>
              <Input value={roomForm.floor} onChange={setRoomField('floor')} className="bg-slate-700 border-slate-600" placeholder="e.g. 1st Floor" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRoomDialogOpen(false)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? 'Adding...' : 'Add Rooms'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Facilities Dialog */}
      <Dialog open={isFacilityDialogOpen} onOpenChange={(open) => { setIsFacilityDialogOpen(open); if (!open) { setEditingFacility(null); setFacilityForm({ name: '', icon: '', type: 0 }); } }}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Features & Amenities</DialogTitle>
            <DialogDescription className="text-slate-400">
              Build your hotel's option list. Then in each room type, select which options are <strong className="text-blue-400">Features</strong> (shown as tags) and which are <strong className="text-purple-400">Amenities</strong> (shown as cards).
            </DialogDescription>
          </DialogHeader>

          {/* Add / Edit inline form */}
          <form onSubmit={handleSaveFacility} className="space-y-3 p-3 bg-slate-700/40 rounded-lg border border-slate-600">
            <div className="flex gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-400">Name</Label>
                <Input
                  value={facilityForm.name}
                  onChange={e => setFacilityForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-700 border-slate-600 h-9"
                  placeholder="e.g. WiFi, Pool Access, Breakfast"
                  required
                />
              </div>
              <div className="w-28 space-y-1">
                <Label className="text-xs text-slate-400">Icon (optional)</Label>
                <Input
                  value={facilityForm.icon}
                  onChange={e => setFacilityForm(prev => ({ ...prev, icon: e.target.value }))}
                  className="bg-slate-700 border-slate-600 h-9"
                  placeholder="e.g. wifi"
                />
              </div>
            </div>

            {/* Type toggle */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Type</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFacilityForm(prev => ({ ...prev, type: 0 }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all border ${
                    facilityForm.type === 0
                      ? 'bg-blue-600 text-white border-blue-500'
                      : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-blue-500'
                  }`}
                >
                  Feature
                  <span className="block text-xs font-normal opacity-70">Tag on room card</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFacilityForm(prev => ({ ...prev, type: 1 }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all border ${
                    facilityForm.type === 1
                      ? 'bg-purple-600 text-white border-purple-500'
                      : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-purple-500'
                  }`}
                >
                  Amenity
                  <span className="block text-xs font-normal opacity-70">Card in room details</span>
                </button>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              {editingFacility && (
                <Button type="button" variant="ghost" onClick={() => { setEditingFacility(null); setFacilityForm({ name: '', icon: '', type: 0 }); }} className="h-8 px-3 text-slate-400 text-sm">
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={facilityDialogSaving} className={`h-8 px-4 text-sm ${facilityForm.type === 1 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {editingFacility ? 'Update' : `Add ${facilityForm.type === 0 ? 'Feature' : 'Amenity'}`}
              </Button>
            </div>
          </form>

          {/* Facility list grouped by type */}
          <div className="space-y-3 mt-2">
            {allFacilities.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No options yet. Add your first one above.</p>
            )}

            {/* Features group */}
            {allFacilities.some(f => f.type === 0) && (
              <div>
                <p className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-1.5">Features</p>
                <div className="space-y-1">
                  {allFacilities.filter(f => f.type === 0).map(f => (
                    <div key={f.id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${editingFacility?.id === f.id ? 'bg-blue-600/20 border border-blue-500/30' : 'bg-slate-700/50'}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-sm text-white">{f.name}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditFacility(f)} className="h-7 w-7 text-slate-400 hover:text-white">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteFacility(f.id)} className="h-7 w-7 text-red-400 hover:text-red-300">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities group */}
            {allFacilities.some(f => f.type === 1) && (
              <div>
                <p className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-1.5">Amenities</p>
                <div className="space-y-1">
                  {allFacilities.filter(f => f.type === 1).map(f => (
                    <div key={f.id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${editingFacility?.id === f.id ? 'bg-purple-600/20 border border-purple-500/30' : 'bg-slate-700/50'}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
                        <span className="text-sm text-white">{f.name}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditFacility(f)} className="h-7 w-7 text-slate-400 hover:text-white">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteFacility(f.id)} className="h-7 w-7 text-red-400 hover:text-red-300">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Rules Dialog — only rendered when dynamic pricing is enabled */}
      <Dialog open={isDynamicPricingEnabled && isPricingDialogOpen} onOpenChange={setIsPricingDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-400" />
              Pricing Rules — {pricingRoomType?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Define date-range pricing overrides. These take priority over the base price during the specified period.
            </DialogDescription>
          </DialogHeader>

          {/* Add Rule button */}
          {!showPricingForm && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => { setEditingPricing(null); setPricingForm(EMPTY_PRICING_FORM); setShowPricingForm(true); }}
                className="bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 border border-amber-600/40"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />Add Rule
              </Button>
            </div>
          )}

          {/* Inline form */}
          {showPricingForm && (
            <form onSubmit={handleSavePricing} className="space-y-3 bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <p className="text-sm font-medium text-amber-400">{editingPricing ? 'Edit Rule' : 'New Pricing Rule'}</p>
              <div className="space-y-2">
                <Label className="text-slate-300">Label <span className="text-slate-500 font-normal">(optional)</span></Label>
                <Input
                  value={pricingForm.label}
                  onChange={e => setPricingForm(p => ({ ...p, label: e.target.value }))}
                  placeholder="e.g. Eid Holiday, Peak Season"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Start Date</Label>
                  <Input
                    type="date"
                    value={pricingForm.startDate}
                    onChange={e => setPricingForm(p => ({ ...p, startDate: e.target.value }))}
                    className="bg-slate-700 border-slate-600"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">End Date</Label>
                  <Input
                    type="date"
                    value={pricingForm.endDate}
                    onChange={e => setPricingForm(p => ({ ...p, endDate: e.target.value }))}
                    className="bg-slate-700 border-slate-600"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Price Per Night (৳)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricingForm.pricePerNight}
                  onChange={e => setPricingForm(p => ({ ...p, pricePerNight: e.target.value }))}
                  placeholder="e.g. 7500"
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={pricingSaving} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                  {pricingSaving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Saving...</> : (editingPricing ? 'Update Rule' : 'Add Rule')}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={cancelPricingForm} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Rules list */}
          {pricingLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
            </div>
          ) : pricingRules.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <TrendingUp className="h-10 w-10 mx-auto mb-2 text-slate-600" />
              <p className="text-sm">No pricing rules yet. Base price <span className="text-white font-medium">৳{pricingRoomType?.basePrice?.toLocaleString()}</span> applies.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pricingRules.map(rule => (
                <div key={rule.id} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-3 border border-slate-600">
                  <div className="space-y-0.5">
                    {rule.label && <p className="text-sm font-medium text-amber-400">{rule.label}</p>}
                    <p className="text-sm text-slate-300">
                      {rule.startDate.substring(0, 10)} → {rule.endDate.substring(0, 10)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold text-base">৳{Number(rule.pricePerNight).toLocaleString()}</span>
                    <Button size="icon" variant="ghost" onClick={() => openEditPricingRule(rule)} className="h-7 w-7 text-slate-400 hover:text-white">
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDeletePricing(rule.id)} disabled={deletingPricingId === rule.id} className="h-7 w-7 text-red-400 hover:text-red-300">
                      {deletingPricingId === rule.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Room Block Management Dialog */}
      <Dialog open={isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-orange-400" />
              Room Blocking — {blockingRoomType?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Block individual rooms for maintenance or any other reason.
            </DialogDescription>
          </DialogHeader>

          {physicalRoomsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
            </div>
          ) : (() => {
            const physicalRoomsForType = (allPhysicalRooms || []).filter(r => r.roomTypeId === blockingRoomType?.id);
            if (physicalRoomsForType.length === 0) {
              return (
                <div className="text-center py-8 text-slate-400">
                  <Lock className="h-10 w-10 mx-auto mb-2 text-slate-600" />
                  <p className="text-sm">No physical rooms added for this type yet.</p>
                  <p className="text-xs mt-1">Use "Add Rooms" to add physical rooms first.</p>
                </div>
              );
            }
            return (
              <div className="space-y-4">
                {/* Room selector */}
                <div className="space-y-2">
                  <Label>Select Room</Label>
                  <Select value={selectedBlockRoomId} onValueChange={handleSelectBlockRoom}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Choose a room number..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {physicalRoomsForType.map(r => (
                        <SelectItem key={r.roomId} value={String(r.roomId)}>
                          Room {r.roomNumber}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedBlockRoomId && (
                  <>
                    {/* Existing blocks */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Active Blocks</Label>
                      {blocksLoading ? (
                        <div className="flex justify-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        </div>
                      ) : currentRoomBlocks.length === 0 ? (
                        <p className="text-xs text-slate-500 py-1">No active blocks for this room.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {currentRoomBlocks.map(b => (
                            <div key={b.id} className="flex items-center justify-between bg-red-900/20 border border-red-700/30 rounded-lg px-3 py-2">
                              <div>
                                <p className="text-sm text-red-300">
                                  {b.fromDate.substring(0, 10)} → {b.toDate.substring(0, 10)}
                                </p>
                                {b.reason && <p className="text-xs text-slate-400">{b.reason}</p>}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUnblock(b.id)}
                                disabled={unblockingId === b.id}
                                className="text-red-400 hover:text-red-300 h-7 px-2 text-xs"
                              >
                                {unblockingId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Unblock'}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Add block form */}
                    <form onSubmit={handleBlockRoom} className="space-y-3 bg-slate-700/40 rounded-lg p-4 border border-slate-600">
                      <p className="text-sm font-medium text-orange-400">Add New Block</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm">From Date</Label>
                          <Input
                            type="date"
                            value={blockForm.fromDate}
                            onChange={e => setBlockForm(p => ({ ...p, fromDate: e.target.value }))}
                            className="bg-slate-700 border-slate-600"
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm">To Date</Label>
                          <Input
                            type="date"
                            value={blockForm.toDate}
                            onChange={e => setBlockForm(p => ({ ...p, toDate: e.target.value }))}
                            className="bg-slate-700 border-slate-600"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Reason <span className="text-slate-500 font-normal">(optional)</span></Label>
                        <Input
                          value={blockForm.reason}
                          onChange={e => setBlockForm(p => ({ ...p, reason: e.target.value }))}
                          placeholder="e.g. Maintenance, Renovation..."
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <Button type="submit" disabled={blockSaving} size="sm" className="bg-orange-600 hover:bg-orange-700">
                        {blockSaving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Blocking...</> : 'Block Room'}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Delete Room Type</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <span className="text-white font-medium">{deletingType?.name}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={handleDeleteType} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Rooms;
